import { ReactElement, useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Calibration } from "./pages/calibration";
import { Home } from "./pages/home";
import { ProtectedQuestionnaires } from "./pages/engines/[engineType]/questionnaires";
import { ProtectedRecording } from "./pages/engines/[engineType]/recording";
import { ProtectedRecordingFile } from "./pages/engines/[engineType]/recording-file";
import { ProtectedSelectRecording } from "./pages/engines/[engineType]/select-recording";
import { Result } from "./pages/engines/[engineType]/results/[fileId]";
import { NotFound } from "./pages/404";
import { AnalysisError } from "./pages/engines/[engineType]/analysis-error";
import { useTranslation } from "react-i18next";
import { Engines } from "./pages/engines/";
import { EngineType } from "./pages/engines/[engineType]";
import { Login } from "./pages/login";
import { useAtom } from "jotai";
import {
  accessTokenInfoAtom,
  currentUserAgreementAtom,
  detectVolumeAtom,
  employeeInfoAtom,
  passwordFlagAtom,
  recordingPatternAtom,
} from "./store";
import dayjs from "dayjs";
import { PasswordReset } from "./pages/password-reset";
import { ResetId } from "./pages/password-reset/[resetId]";
import { AccountUnlocked } from "./pages/account-unlocked";
import { isValidLanguageType } from "./types";
import { ImageProvider } from "./components/atoms/ImageProvider";
import { APP_TYPE } from "./environments";
import UIX_FAVICON from "./assets/favicon_uix.ico";
import { SentryRoutes } from "../sentry";
import * as Sentry from "@sentry/react";
import { UserAgreement } from "./userAgreement";
import DebugTools from "./DebugTools";
import { PasswordChangeForm } from "./pages/password/change_password";

// ページ遷移時にスクロール位置をリセットする
function ScrollToTop(): ReactElement | null {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function ProtectedRoutes(): ReactElement {
  const [tokenInfo] = useAtom(accessTokenInfoAtom);
  const { i18n } = useTranslation();
  const [employeeInfo] = useAtom(employeeInfoAtom);
  const [passwordFlag] = useAtom(passwordFlagAtom);
  const [hasAgreed] = useAtom(currentUserAgreementAtom);
  const [detectVolume] = useAtom(detectVolumeAtom);
  const [recordingPattern] = useAtom(recordingPatternAtom);
  const location = useLocation();
  const now = dayjs();
  const isValid =
    tokenInfo &&
    dayjs(tokenInfo.expired_at).isAfter(now) &&
    passwordFlag !== null;
  // NOTE: トークンが設定されていてpasswordFlagは設定されているはずだが、未設定の場合はログイン画面に戻す

  const path = location.pathname;

  if (employeeInfo.employee_id) {
    Sentry.setUser({ email: employeeInfo.employee_id });
  }

  // トークンが取得できない場合は、ログイン画面に遷移
  // NOTE: 蓼科アプリでは結果画面はトークン不要
  // NOTE: 結果画面リロードした場合にログインに行くようにrecordingPatternもチェック
  if (!isValid) {
    if (!path.includes("/results")) {
      return <Navigate to={`/${i18n.language}/login`} />;
    } else {
      // 結果画面でリロードされたらログイン画面へ
      if (recordingPattern === 0) {
        return <Navigate to={`/${i18n.language}/login`} />;
      } else {
        return <Outlet />;
      }
    }
  }

  // パスワードフラグチェック
  if (!passwordFlag) {
    if (path.includes("/password")) {
      return <Outlet />;
    } else {
      return <Navigate to={`/${i18n.language}/password`} replace />;
    }
  }

  // 同意済みか確認
  if (!hasAgreed) {
    if (path.includes("/user-agreement")) {
      return <Outlet />;
    } else {
      return <Navigate to={`/${i18n.language}/user-agreement`} replace />;
    }
  }

  // 音量閾値決定済か確認
  if (detectVolume < 0) {
    if (path.includes("/calibration")) {
      return <Outlet />;
    } else {
      return <Navigate to={`/${i18n.language}/calibration`} replace />;
    }
  }

  // 蓼科アプリでは、録音パターンATOMが削除された場合はホーム画面に遷移
  if (recordingPattern === 0 && path.includes("/recording")) {
    return <Navigate to={`/${i18n.language}/home`} replace />;
  }

  return <Outlet />;
}

function AppRoot(): ReactElement {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isValidLanguageType(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang).then();
      }
    } else {
      navigate("/404", { replace: true });
    }
  }, [i18n, lang, navigate]);

  return <Outlet />;
}

function App(): ReactElement {
  const { i18n } = useTranslation();
  const defaultLanguage = isValidLanguageType(i18n.language)
    ? i18n.language
    : "en";
  const defaultPath = `/${defaultLanguage}/home`;

  // 環境に応じてファビコンを表示
  useEffect(() => {
    if (APP_TYPE === "uix") {
      const link = document.querySelector("link[rel*='icon']");
      if (link instanceof HTMLLinkElement) {
        link.href = UIX_FAVICON;
      }
    }
  }, []);

  return (
    <ImageProvider>
      <ScrollToTop />
      <SentryRoutes>
        <Route path="/" element={<Navigate replace to={defaultPath} />} />
        <Route path=":lang" element={<AppRoot />}>
          <Route index element={<Navigate replace to={defaultPath} />} />
          <Route path="login" element={<Login />} />
          <Route path="account-unlocked" element={<AccountUnlocked />} />
          <Route path="password-reset">
            <Route index element={<PasswordReset />} />
            <Route path=":resetId" element={<ResetId />} />
          </Route>

          <Route element={<ProtectedRoutes />}>
            <Route path="password" element={<PasswordChangeForm />} />
            <Route path="home" element={<Home />} />
            <Route path="calibration" element={<Calibration />} />
            <Route path="user-agreement" element={<UserAgreement />} />
            <Route path="engines">
              <Route index element={<Engines />} />
              <Route path=":engineType" element={<EngineType />}>
                <Route index element={<Navigate replace to="/404" />} />
                <Route
                  path="select-recording"
                  element={<ProtectedSelectRecording />}
                />
                <Route
                  path="questionnaires"
                  element={<ProtectedQuestionnaires />}
                />
                <Route path="recording" element={<ProtectedRecording />} />
                <Route
                  path="recording-file"
                  element={<ProtectedRecordingFile />}
                />
                <Route path="analysis-error" element={<AnalysisError />} />
                <Route path="results">
                  <Route index element={<Navigate replace to="/404" />} />
                  <Route path=":fileId" element={<Result />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate replace to="/404" />} />
      </SentryRoutes>
      <DebugTools />
    </ImageProvider>
  );
}

export default App;
